(() => {
  const instances = new Map();
  const sliderUtils = window.AWSliderCoreUtils;
  const STORAGE_KEY = "aw_recently_viewed_products";
  const GENDER_STORAGE_KEY = "aw_recently_viewed_product_gender_badges";
  const MAX_STORED_PRODUCTS = 20;
  const WISHLIST_SYNC_EVENT = "aw:collection-product-card-wishlist-sync";

  class AWRecentlyViewed {
    constructor(root) {
      this.root = root;
      this.sourceType = root.dataset.sourceType || "collection";
      this.currentProductHandle = root.dataset.currentProductHandle || "";
      this.productLimit = parseInt(root.dataset.productLimit || "4", 10) || 4;
      this.isDesignMode = root.dataset.designMode === "true";
      this.showGenderBadge = root.dataset.showGenderBadge === "true";
      this.showWishlist = root.dataset.showWishlist === "true";
      this.wishlistIconDefault = root.dataset.wishlistIconDefault || "";
      this.viewport = root.querySelector("[data-aw-product-recommend-items-viewport]");
      this.list = root.querySelector("[data-aw-product-recommend-items-list]");
      this.scrollbar = root.querySelector("[data-aw-product-recommend-items-scrollbar]");
      this.thumb = root.querySelector("[data-aw-product-recommend-items-scrollbar-thumb]");
      this.empty = root.querySelector("[data-aw-product-recommend-items-empty]");
      this.fallbackProducts = this.getFallbackProducts();
      this.rafId = null;
      this.resizeId = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onImageLoad = this.onImageLoad.bind(this);

      this.saveCurrentProduct();
      this.bindEvents();

      if (this.sourceType === "recently_viewed") {
        this.renderRecentlyViewed();
      } else {
        this.bindImageLoad();
        this.update();
      }
    }

    bindEvents() {
      if (this.viewport) {
        this.viewport.addEventListener("scroll", this.onScroll, { passive: true });
      }

      window.addEventListener("resize", this.onResize);
    }

    bindImageLoad() {
      sliderUtils.bindImageLoad(this.root, this.onImageLoad);
    }

    destroy() {
      if (this.viewport) {
        this.viewport.removeEventListener("scroll", this.onScroll);
      }

      window.removeEventListener("resize", this.onResize);

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      if (this.resizeId) {
        window.cancelAnimationFrame(this.resizeId);
        this.resizeId = null;
      }
    }

    getStoredHandles() {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        const handles = stored ? JSON.parse(stored) : [];

        return Array.isArray(handles) ? handles : [];
      } catch (_error) {
        return [];
      }
    }

    setStoredHandles(handles) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED_PRODUCTS)));
      } catch (_error) {
        // localStorage unavailable
      }
    }

    getStoredGenderBadges() {
      try {
        const stored = window.localStorage.getItem(GENDER_STORAGE_KEY);
        const genderBadges = stored ? JSON.parse(stored) : {};

        return genderBadges && typeof genderBadges === "object" && !Array.isArray(genderBadges)
          ? genderBadges
          : {};
      } catch (_error) {
        return {};
      }
    }

    setStoredGenderBadges(genderBadges) {
      try {
        window.localStorage.setItem(GENDER_STORAGE_KEY, JSON.stringify(genderBadges));
      } catch (_error) {
        // localStorage unavailable
      }
    }

    getCurrentProductGenderBadges() {
      try {
        const genderValues = JSON.parse(this.root.dataset.currentProductGenderValues || "null");

        return this.getGenderBadgesFromValues(genderValues);
      } catch (_error) {
        return null;
      }
    }

    getGenderBadgesFromValues(values) {
      const genderText = Array.isArray(values)
        ? values.join(",")
        : typeof values === "string"
          ? values
          : "";

      if (!genderText) {
        return null;
      }

      return {
        mens: genderText.includes("\u30e1\u30f3\u30ba"),
        ladies: genderText.includes("\u30ec\u30c7\u30a3\u30b9") || genderText.includes("\u30ec\u30c7\u30a3\u30fc\u30b9"),
        unisex: genderText.includes("\u30e6\u30cb\u30bb\u30c3\u30af\u30b9")
      };
    }

    saveCurrentProduct() {
      if (!this.currentProductHandle) {
        return;
      }

      const handles = this.getStoredHandles().filter((handle) => handle !== this.currentProductHandle);
      handles.unshift(this.currentProductHandle);
      this.setStoredHandles(handles);

      const currentGenderBadges = this.getCurrentProductGenderBadges();

      if (currentGenderBadges) {
        const storedGenderBadges = this.getStoredGenderBadges();
        const validHandles = new Set(handles);

        storedGenderBadges[this.currentProductHandle] = currentGenderBadges;

        Object.keys(storedGenderBadges).forEach((handle) => {
          if (!validHandles.has(handle)) {
            delete storedGenderBadges[handle];
          }
        });

        this.setStoredGenderBadges(storedGenderBadges);
      }
    }

    getFallbackProducts() {
      const element = this.root.querySelector("[data-aw-product-recommend-items-fallback-products]");

      if (!element) {
        return [];
      }

      try {
        const products = JSON.parse(element.textContent || "[]");
        return Array.isArray(products) ? products : [];
      } catch (_error) {
        return [];
      }
    }

    async renderRecentlyViewed() {
      if (!this.list) {
        return;
      }

      const handles = this.getStoredHandles()
        .filter((handle) => handle && handle !== this.currentProductHandle)
        .slice(0, this.productLimit);

      const storedGenderBadges = this.getStoredGenderBadges();
      const products = await Promise.all(handles.map(async (handle) => {
        const product = await this.fetchProduct(handle);

        if (!product) {
          return null;
        }

        const genderBadges = this.showGenderBadge
          ? storedGenderBadges[handle] || await this.fetchGenderBadges(handle)
          : null;

        return {
          ...product,
          gender_badges: genderBadges
        };
      }));
      const validProducts = products.filter(Boolean);
      const combinedProducts = [];
      const usedHandles = new Set();
      const fallbackProductsByHandle = new Map(
        this.fallbackProducts.map((product) => [product && product.handle, product])
      );

      validProducts.forEach((product) => {
        const handle = product && product.handle;

        if (!handle || usedHandles.has(handle) || handle === this.currentProductHandle) {
          return;
        }

        usedHandles.add(handle);
        combinedProducts.push({
          ...product,
          gender_badges:
            product.gender_badges
            || this.getGenderBadgesFromValues(product.gender_values)
            || this.getGenderBadgesFromValues(fallbackProductsByHandle.get(handle)?.gender_values)
        });
      });

      if (!combinedProducts.length) {
        this.showEmptyState();
        return;
      }

      this.list.innerHTML = combinedProducts.map((product) => this.createProductCard(product)).join("");
      window.dispatchEvent(new CustomEvent(WISHLIST_SYNC_EVENT));

      if (this.empty) {
        this.empty.hidden = true;
      }

      if (this.viewport) {
        this.viewport.hidden = false;
      }

      if (this.scrollbar) {
        this.scrollbar.hidden = false;
      }

      this.root.hidden = false;
      this.bindImageLoad();
      this.update();
    }

    async fetchProduct(handle) {
      try {
        const response = await window.fetch(`/products/${handle}.js`, {
          credentials: "same-origin"
        });

        if (!response.ok) {
          return null;
        }

        return await response.json();
      } catch (_error) {
        return null;
      }
    }

    async fetchGenderBadges(handle) {
      try {
        const response = await window.fetch(
          `/products/${encodeURIComponent(handle)}?view=gender-badges`,
          { credentials: "same-origin" }
        );

        if (!response.ok) {
          return null;
        }

        const genderBadges = await response.json();

        if (Array.isArray(genderBadges) || typeof genderBadges === "string") {
          return this.getGenderBadgesFromValues(genderBadges);
        }

        if (!genderBadges || typeof genderBadges !== "object") {
          return null;
        }

        return genderBadges.values !== undefined
          ? this.getGenderBadgesFromValues(genderBadges.values)
          : genderBadges;
      } catch (_error) {
        return null;
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents);
      }

      return `¥${Math.round(cents / 100).toLocaleString("ja-JP")}`;
    }

    createProductCard(product) {
      const image = product.featured_image || (Array.isArray(product.images) ? product.images[0] : "") || "";
      const imageAlt = product.featured_image_alt || product.title || "";
      const title = product.title || "";
      const url = product.url || `/products/${product.handle}`;
      const price = typeof product.price === "number" ? product.price : 0;
      const genderBadgeLabels = this.getGenderBadgeLabels(product);
      const genderBadges = genderBadgeLabels.length
        ? `<div class="aw-product-gender-badges">${genderBadgeLabels
            .map((label) => `<span class="aw-product-gender-badge aw-collection-product-card__item-badge">${label}</span>`)
            .join("")}</div>`
        : "";
      const itemClasses = ["aw-product-recommend-items__item"];

      if (this.showWishlist) {
        itemClasses.push("aw-product-grid-item");
      }

      return `
        <article class="${itemClasses.join(" ")}" data-aw-product-recommend-items-item>
          <div class="aw-product-recommend-items__media">
            <a class="aw-product-recommend-items__link" href="${this.escapeHtml(url)}">
              <div class="aw-product-recommend-items__image-wrap">
                ${
                  image
                    ? `<img class="aw-product-recommend-items__image" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(imageAlt)}" loading="lazy" decoding="async">`
                    : `<div class="aw-product-recommend-items__placeholder"></div>`
                }
              </div>
            </a>
          </div>
          <a class="aw-product-recommend-items__link aw-product-recommend-items__link--body" href="${this.escapeHtml(url)}">
            <div class="aw-product-recommend-items__body">
              ${genderBadges}
              <h3 class="aw-product-recommend-items__title">${this.escapeHtml(title)}</h3>
              <p class="aw-product-recommend-items__price">${this.escapeHtml(this.formatMoney(price))}</p>
            </div>
          </a>
        </article>
      `;
    }

    getGenderBadgeLabels(product) {
      if (!this.showGenderBadge || !product?.gender_badges) {
        return [];
      }

      const labels = [];

      if (product.gender_badges.mens) {
        labels.push("MENS");
      }

      if (product.gender_badges.ladies) {
        labels.push("LADIES");
      }

      if (product.gender_badges.unisex) {
        labels.push("UNISEX");
      }

      return labels;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }

    showEmptyState() {
      this.root.hidden = true;

      if (this.empty) {
        this.empty.hidden = true;
      }

      if (this.viewport) {
        this.viewport.hidden = true;
      }

      if (this.scrollbar) {
        this.scrollbar.hidden = true;
      }
    }

    onScroll() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    onResize() {
      if (this.resizeId) {
        return;
      }

      this.resizeId = window.requestAnimationFrame(() => {
        this.resizeId = null;
        this.update();
      });
    }

    onImageLoad() {
      this.update();
    }

    update() {
      this.updateScrollbar();
    }

    updateScrollbar() {
      if (!this.viewport || !this.scrollbar || !this.thumb) {
        return;
      }

      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const current = this.viewport.scrollLeft;
      const hasOverflow = maxScroll > 1;

      sliderUtils.updateScrollbar({
        viewport: this.viewport,
        scrollbar: this.scrollbar,
        thumb: this.thumb,
        enabled: hasOverflow
      });
    }
  }

  const init = (container = document) => {
    container.querySelectorAll("[data-aw-product-recommend-items]").forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      instances.set(root, new AWRecentlyViewed(root));
    });
  };

  const destroy = (container) => {
    container.querySelectorAll("[data-aw-product-recommend-items]").forEach((root) => {
      const instance = instances.get(root);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(root);
    });
  };

  document.addEventListener("DOMContentLoaded", () => init());

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });
})();
